import { useId, useState } from 'react';
import type * as React from 'react';
import { Link } from 'react-router';
import { Search } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { CursorFooter } from '@/components/cursor-footer';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Input, Label, NativeSelect } from '@/components/ui/field';
import { Badge, Mono } from '@/components/ui/primitives';
import { RESOURCES, type AdminUserSummary, type UserAccountStatus } from '@/api/resources';
import { useCursorPage } from '@/hooks/use-cursor-page';
import { formatDate, formatNumber } from '@/lib/format';

const STATUSES: readonly UserAccountStatus[] = ['PENDING', 'ACTIVE', 'SUSPENDED', 'DELETED'];

/**
 * User lookup (06 §2) — `SUPPORT` and `SUPER_ADMIN`.
 *
 * ⚠️ Roles are enforced by the server. `AdminPanelUsersController` carries
 * `@AdminRoles('SUPER_ADMIN', 'SUPPORT')` and answers `403 INSUFFICIENT_ROLE` to
 * anyone else — `ANALYST` above all, whose whole definition is analytics with no
 * PII (06 §1). The sidebar hiding this screen is a courtesy, not the control.
 *
 * ⚠️ Search includes soft-deleted accounts on purpose. Deletion retains the
 * email address permanently, so the person can never register again and
 * reactivation is their only route back (06 §2, 02 §3.2) — support cannot
 * reactivate an account they cannot find.
 *
 * ⚠️ No child names anywhere on this screen. `childCount` is a number the API
 * produces without fetching a single name; the names live behind the audited
 * reveal on the detail screen, one child at a time, with a stated reason.
 */
export function UsersPage(): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<UserAccountStatus | ''>('');
  const searchId = useId();
  const statusId = useId();

  const page = useCursorPage<AdminUserSummary>(RESOURCES.users, {
    q: q || undefined,
    status: status || undefined,
    limit: 25,
  });

  const columns: Array<Column<AdminUserSummary>> = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (row) => `${row.lastName} ${row.firstName}`,
      render: (row) => (
        <Link to={`/users/${row.id}`} className="rounded hover:underline">
          <span className="block font-medium text-ink">
            {row.firstName} {row.lastName}
          </span>
          <span className="block text-xs text-ink-soft">{row.email}</span>
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      sortValue: (row) => row.status,
      render: (row) => <StatusBadge status={row.status} deletedAt={row.deletedAt} />,
    },
    {
      key: 'verified',
      header: 'Verified',
      hideBelow: 'md',
      sortValue: (row) => row.emailVerifiedAt ?? '',
      render: (row) =>
        row.emailVerifiedAt ? (
          <time dateTime={row.emailVerifiedAt}>{formatDate(row.emailVerifiedAt)}</time>
        ) : (
          <span className="text-xs text-ink-faint">Not verified</span>
        ),
    },
    {
      key: 'childCount',
      header: 'Children',
      align: 'right',
      width: '6rem',
      sortValue: (row) => row.childCount,
      render: (row) => formatNumber(row.childCount),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      hideBelow: 'lg',
      sortValue: (row) => row.createdAt,
      render: (row) => <time dateTime={row.createdAt}>{formatDate(row.createdAt)}</time>,
    },
    {
      key: 'id',
      header: 'User id',
      hideBelow: 'xl',
      render: (row) => <Mono>{row.id}</Mono>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '6rem',
      render: (row) => (
        <Button asChild size="sm" variant="secondary">
          <Link to={`/users/${row.id}`}>Open</Link>
        </Button>
      ),
    },
  ];

  return (
    <Page
      title="Users"
      description="Find an account by email, name or id. Deleted accounts are included."
      breadcrumbs={[{ label: 'People' }, { label: 'Users' }]}
      width="wide"
    >
      <div className="space-y-5">
        <Notice title="Child data is hidden until you reveal it">
          Names, photos and dates of birth are withheld on every screen in this panel. Revealing one
          is a separate request that records your identity and the reason you give.
        </Notice>

        <form
          className="flex flex-wrap items-end gap-3 rounded-card border border-line bg-surface px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault();
            setQ(draft.trim());
          }}
        >
          <div className="min-w-64 flex-1 space-y-1.5">
            <Label htmlFor={searchId}>Search</Label>
            <Input
              id={searchId}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Email, name or user id"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={statusId}>Status</Label>
            <NativeSelect
              id={statusId}
              className="w-40"
              value={status}
              onChange={(event) => setStatus(event.target.value as UserAccountStatus | '')}
            >
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value.charAt(0) + value.slice(1).toLowerCase()}
                </option>
              ))}
            </NativeSelect>
          </div>

          <Button type="submit" variant="primary">
            <Search aria-hidden="true" />
            Search
          </Button>
        </form>

        <DataTable
          label="Users"
          columns={columns}
          rows={page.rows}
          rowKey={(row) => row.id}
          isLoading={page.isLoading}
          error={page.error}
          onRetry={page.refetch}
          endpoint="GET /admin/users"
          empty={
            <EmptyState
              title={q ? 'No accounts match that' : 'No accounts yet'}
              description={
                q
                  ? 'Try the full email address, or clear the status filter — deleted accounts are included by default.'
                  : 'Search by email, name or user id above to find the account a support case is about.'
              }
              action={
                q ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDraft('');
                      setQ('');
                      setStatus('');
                    }}
                  >
                    Clear the search
                  </Button>
                ) : undefined
              }
            />
          }
          footer={
            <CursorFooter
              page={page}
              note="Every read on this screen is recorded in the audit log."
            />
          }
        />
      </div>
    </Page>
  );
}

export function StatusBadge({
  status,
  deletedAt,
}: {
  status: UserAccountStatus;
  deletedAt: string | null;
}): React.JSX.Element {
  // `deletedAt` wins over `status`: a soft-deleted account can still carry the
  // status it had before deletion, and "Active" on a deleted row would send
  // support down entirely the wrong path.
  if (deletedAt) return <Badge tone="danger">Deleted</Badge>;
  if (status === 'ACTIVE') return <Badge tone="success">Active</Badge>;
  if (status === 'SUSPENDED') return <Badge tone="danger">Suspended</Badge>;
  if (status === 'PENDING') return <Badge tone="caution">Pending</Badge>;
  return <Badge tone="neutral">{status.toLowerCase()}</Badge>;
}
